/**
 * Dino-themed SVG decorations — strong JP brand colors, no transparency.
 * Colors: JP Red #CC0000, Amber #D4A017, Charcoal #1C1917
 */

const RED = '#CC0000';
const AMBER = '#D4A017';
const CHARCOAL = '#1C1917';

/* ─── Three-toed Theropod Footprint (realistic with pad detail) ─── */

export const FootprintSmall = ({ className = '' }: { className?: string }) => (
  <svg className={className} width="50" height="70" viewBox="0 0 50 70" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Center toe */}
    <path d="M25 2 C27 2 30 6 30 12 C30 18 28 22 25 22 C22 22 20 18 20 12 C20 6 23 2 25 2Z" fill={RED} />
    <ellipse cx="25" cy="6" rx="2" ry="1.5" fill={RED} opacity="0.7" />
    {/* Left toe */}
    <path d="M12 10 C14 8 18 9 20 14 C22 19 21 24 18 25 C15 26 12 23 10 18 C8 13 10 12 12 10Z" fill={RED} />
    {/* Right toe */}
    <path d="M38 10 C36 8 32 9 30 14 C28 19 29 24 32 25 C35 26 38 23 40 18 C42 13 40 12 38 10Z" fill={RED} />
    {/* Main heel pad */}
    <path d="M25 30 C33 30 40 36 40 44 C40 52 35 58 25 58 C15 58 10 52 10 44 C10 36 17 30 25 30Z" fill={RED} />
    {/* Pad texture lines */}
    <path d="M18 40 Q25 38 32 40" stroke={CHARCOAL} strokeWidth="0.8" opacity="0.2" />
    <path d="M16 46 Q25 44 34 46" stroke={CHARCOAL} strokeWidth="0.8" opacity="0.2" />
    <path d="M18 52 Q25 50 32 52" stroke={CHARCOAL} strokeWidth="0.8" opacity="0.2" />
  </svg>
);

export const FootprintLarge = ({ className = '' }: { className?: string }) => (
  <svg className={className} width="80" height="110" viewBox="0 0 80 110" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Center toe with claw tip */}
    <path d="M40 2 L42 0 L44 4 C46 10 46 20 44 28 C42 34 40 36 38 36 C36 36 34 34 32 28 C30 20 30 10 32 4 L34 0 L36 2 Z" fill={RED} />
    {/* Left toe */}
    <path d="M16 16 L13 13 L16 14 C22 16 26 22 28 30 C30 36 28 40 24 42 C20 44 16 40 14 34 C12 26 12 20 16 16Z" fill={RED} />
    {/* Right toe */}
    <path d="M64 16 L67 13 L64 14 C58 16 54 22 52 30 C50 36 52 40 56 42 C60 44 64 40 66 34 C68 26 68 20 64 16Z" fill={RED} />
    {/* Big heel pad with shape */}
    <path d="M40 50 C52 50 62 58 62 70 C62 82 54 92 40 92 C26 92 18 82 18 70 C18 58 28 50 40 50Z" fill={RED} />
    {/* Inner pad detail */}
    <ellipse cx="40" cy="68" rx="14" ry="10" fill={CHARCOAL} opacity="0.12" />
    <path d="M28 74 Q40 70 52 74" stroke={CHARCOAL} strokeWidth="1" opacity="0.15" />
    <path d="M30 80 Q40 77 50 80" stroke={CHARCOAL} strokeWidth="1" opacity="0.15" />
  </svg>
);

export const FootprintTrail = ({ className = '' }: { className?: string }) => (
  <svg className={className} width="160" height="120" viewBox="0 0 160 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Left foot */}
    <g transform="translate(5, 25) rotate(-18 25 35)">
      <path d="M25 2 C27 2 30 6 30 12 C30 18 28 22 25 22 C22 22 20 18 20 12 C20 6 23 2 25 2Z" fill={AMBER} />
      <path d="M12 10 C14 8 18 9 20 14 C22 19 21 24 18 25 C15 26 12 23 10 18 C8 13 10 12 12 10Z" fill={AMBER} />
      <path d="M38 10 C36 8 32 9 30 14 C28 19 29 24 32 25 C35 26 38 23 40 18 C42 13 40 12 38 10Z" fill={AMBER} />
      <path d="M25 30 C33 30 40 36 40 44 C40 52 35 58 25 58 C15 58 10 52 10 44 C10 36 17 30 25 30Z" fill={AMBER} />
    </g>
    {/* Right foot */}
    <g transform="translate(80, 5) rotate(12 25 35)">
      <path d="M25 2 C27 2 30 6 30 12 C30 18 28 22 25 22 C22 22 20 18 20 12 C20 6 23 2 25 2Z" fill={AMBER} />
      <path d="M12 10 C14 8 18 9 20 14 C22 19 21 24 18 25 C15 26 12 23 10 18 C8 13 10 12 12 10Z" fill={AMBER} />
      <path d="M38 10 C36 8 32 9 30 14 C28 19 29 24 32 25 C35 26 38 23 40 18 C42 13 40 12 38 10Z" fill={AMBER} />
      <path d="M25 30 C33 30 40 36 40 44 C40 52 35 58 25 58 C15 58 10 52 10 44 C10 36 17 30 25 30Z" fill={AMBER} />
    </g>
  </svg>
);

/* ─── Claw Scrapes (4-slash ripped marks) ─── */

export const ClawScratch3 = ({ className = '' }: { className?: string }) => (
  <svg className={className} width="100" height="150" viewBox="0 0 100 150" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Slash 1 — tapered shape */}
    <path d="M18 0 C16 4 12 20 10 40 C8 60 10 80 12 100 C14 120 10 140 8 150 L14 150 C16 138 18 118 16 98 C14 78 12 58 14 38 C16 18 20 4 22 0Z" fill={CHARCOAL} />
    {/* Slash 2 */}
    <path d="M36 0 C34 6 30 24 28 46 C26 68 28 88 30 108 C32 128 28 142 26 150 L34 150 C36 140 38 126 36 106 C34 86 32 66 34 44 C36 22 38 6 40 0Z" fill={CHARCOAL} />
    {/* Slash 3 */}
    <path d="M56 0 C54 8 50 28 48 52 C46 74 48 92 50 112 C52 132 48 144 46 150 L54 150 C56 142 58 130 56 110 C54 90 52 72 54 50 C56 26 58 8 60 0Z" fill={CHARCOAL} />
    {/* Slash 4 — thinner */}
    <path d="M74 2 C72 8 70 26 68 48 C66 68 68 86 70 106 C72 126 68 142 66 150 L72 150 C74 140 76 124 74 104 C72 84 70 66 72 46 C74 24 76 8 78 2Z" fill={CHARCOAL} />
  </svg>
);

export const ClawScratchDiagonal = ({ className = '' }: { className?: string }) => (
  <svg className={className} width="130" height="110" viewBox="0 0 130 110" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Diagonal slash 1 */}
    <path d="M2 8 C10 12 28 24 48 40 C68 56 86 72 104 86 C112 92 120 98 126 102 L128 98 C122 92 112 84 102 78 C84 64 66 48 46 32 C26 16 10 6 4 4Z" fill={CHARCOAL} />
    {/* Diagonal slash 2 */}
    <path d="M6 0 C14 6 32 20 52 36 C72 52 90 68 108 82 C116 88 122 94 128 98 L130 94 C126 88 116 80 106 74 C88 60 70 44 50 28 C30 12 14 2 8 -2Z" fill={CHARCOAL} />
    {/* Diagonal slash 3 */}
    <path d="M0 18 C8 22 24 34 44 50 C64 66 82 80 100 94 C108 100 116 104 122 108 L124 104 C118 98 108 92 98 86 C80 72 62 58 42 42 C22 26 8 16 2 14Z" fill={CHARCOAL} />
  </svg>
);

export const ClawScratchShort = ({ className = '' }: { className?: string }) => (
  <svg className={className} width="70" height="100" viewBox="0 0 70 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 0 C12 6 8 22 8 42 C8 62 10 78 8 100 L14 100 C16 76 14 60 14 40 C14 20 16 6 18 0Z" fill={CHARCOAL} />
    <path d="M32 0 C30 8 26 26 26 48 C26 70 28 84 26 100 L34 100 C36 82 34 68 34 46 C34 24 36 8 38 0Z" fill={CHARCOAL} />
    <path d="M52 0 C50 6 48 24 48 44 C48 64 50 80 48 100 L54 100 C56 78 54 62 54 42 C54 22 56 6 58 0Z" fill={CHARCOAL} />
  </svg>
);

/* ─── Ammonite Fossil (spiral shell) ─── */

export const AmmoniteFossil = ({ className = '' }: { className?: string }) => (
  <svg className={className} width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Outer shell spiral */}
    <path d="M40 4 C58 4 72 18 72 36 C72 54 58 68 40 68 C26 68 14 58 10 44" stroke={AMBER} strokeWidth="4" fill="none" />
    <path d="M10 44 C8 36 12 26 20 20 C28 14 38 14 44 20" stroke={AMBER} strokeWidth="3.5" fill="none" />
    <path d="M44 20 C50 26 50 34 46 40 C42 46 34 48 28 44" stroke={AMBER} strokeWidth="3" fill="none" />
    <path d="M28 44 C24 40 24 34 28 30 C32 26 38 26 40 30" stroke={AMBER} strokeWidth="2.5" fill="none" />
    <path d="M40 30 C42 34 40 38 36 38 C34 38 32 36 34 34" stroke={AMBER} strokeWidth="2" fill="none" />
    {/* Ribbing lines */}
    <path d="M56 8 L62 18" stroke={AMBER} strokeWidth="1.5" opacity="0.6" />
    <path d="M66 20 L70 32" stroke={AMBER} strokeWidth="1.5" opacity="0.6" />
    <path d="M68 40 L66 52" stroke={AMBER} strokeWidth="1.5" opacity="0.6" />
    <path d="M58 60 L48 66" stroke={AMBER} strokeWidth="1.5" opacity="0.6" />
    <path d="M34 68 L22 62" stroke={AMBER} strokeWidth="1.5" opacity="0.6" />
  </svg>
);

/* ─── Dino Bone (detailed femur shape) ─── */

export const BoneIcon = ({ className = '' }: { className?: string }) => (
  <svg className={className} width="90" height="35" viewBox="0 0 90 35" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Left knob top */}
    <circle cx="12" cy="8" r="7" fill={AMBER} />
    {/* Left knob bottom */}
    <circle cx="10" cy="24" r="6" fill={AMBER} />
    {/* Shaft */}
    <path d="M16 10 C20 12 70 12 74 10 L76 14 C72 16 20 18 16 16 Z" fill={AMBER} />
    <path d="M14 18 C18 20 72 20 76 18 L78 22 C74 24 18 24 14 22 Z" fill={AMBER} />
    {/* Right knob top */}
    <circle cx="80" cy="9" r="7" fill={AMBER} />
    {/* Right knob bottom */}
    <circle cx="78" cy="25" r="6" fill={AMBER} />
    {/* Shaft crack detail */}
    <path d="M35 13 L38 18 L34 22" stroke={CHARCOAL} strokeWidth="0.8" opacity="0.3" />
    <path d="M55 13 L52 17 L56 22" stroke={CHARCOAL} strokeWidth="0.8" opacity="0.3" />
  </svg>
);

/* ─── Fern Fossil (prehistoric plant imprint) ─── */

export const LeafFossil = ({ className = '' }: { className?: string }) => (
  <svg className={className} width="60" height="90" viewBox="0 0 60 90" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Main stem */}
    <path d="M30 5 C30 15 30 75 30 88" stroke={AMBER} strokeWidth="2.5" strokeLinecap="round" />
    {/* Left fronds */}
    <path d="M30 15 C24 12 14 10 8 14 C14 14 22 16 30 20" fill={AMBER} opacity="0.8" />
    <path d="M30 28 C24 25 12 24 6 28 C12 28 22 30 30 34" fill={AMBER} opacity="0.7" />
    <path d="M30 42 C24 39 14 38 8 42 C14 42 22 44 30 48" fill={AMBER} opacity="0.6" />
    <path d="M30 56 C26 53 18 52 14 56 C18 56 24 58 30 62" fill={AMBER} opacity="0.5" />
    {/* Right fronds */}
    <path d="M30 15 C36 12 46 10 52 14 C46 14 38 16 30 20" fill={AMBER} opacity="0.8" />
    <path d="M30 28 C36 25 48 24 54 28 C48 28 38 30 30 34" fill={AMBER} opacity="0.7" />
    <path d="M30 42 C36 39 46 38 52 42 C46 42 38 44 30 48" fill={AMBER} opacity="0.6" />
    <path d="M30 56 C34 53 42 52 46 56 C42 56 36 58 30 62" fill={AMBER} opacity="0.5" />
    {/* Tip */}
    <path d="M30 5 C28 2 30 0 32 2 Z" fill={AMBER} />
  </svg>
);

/* ─── T-Rex Skull Silhouette ─── */

export const DinoSkull = ({ className = '' }: { className?: string }) => (
  <svg className={className} width="70" height="55" viewBox="0 0 70 55" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Skull outline */}
    <path d="M10 30 C10 14 22 4 38 4 C50 4 60 10 64 20 C66 26 66 32 62 36 L58 36 L56 32 L52 36 L48 32 L44 36 L40 32 L36 34 C30 38 20 40 14 38 C10 36 10 34 10 30Z" fill={RED} />
    {/* Eye socket */}
    <circle cx="28" cy="18" r="6" fill="#FFF5E6" />
    <circle cx="28" cy="18" r="3" fill={CHARCOAL} />
    {/* Nostril */}
    <ellipse cx="48" cy="14" rx="3" ry="2" fill={CHARCOAL} opacity="0.6" />
    {/* Jaw */}
    <path d="M14 36 C14 38 16 44 24 48 C32 52 44 50 52 46 C56 44 58 40 58 38 L54 38 L52 42 L48 38 L44 42 L40 38 L36 42 L32 38 L28 40 L24 38 L20 40 L16 36Z" fill={RED} />
    {/* Teeth */}
    <path d="M36 34 L37 38 L38 34" fill="white" />
    <path d="M40 32 L41 37 L42 32" fill="white" />
    <path d="M44 36 L45 40 L46 36" fill="white" />
    <path d="M48 32 L49 37 L50 32" fill="white" />
    <path d="M52 36 L53 40 L54 36" fill="white" />
    <path d="M56 32 L57 36 L58 32" fill="white" />
  </svg>
);

/* ─── Section Divider Variants ─── */

export const DividerFootprints = ({ className = '' }: { className?: string }) => (
  <div className={`w-full overflow-hidden relative h-10 ${className}`}>
    <FootprintSmall className="absolute left-[25%] top-0 rotate-[-15deg] w-6 h-8 opacity-30" />
    <FootprintSmall className="absolute left-[50%] top-1 rotate-[10deg] w-6 h-8 opacity-25" />
    <FootprintSmall className="absolute left-[75%] -top-1 rotate-[-8deg] w-6 h-8 opacity-30" />
  </div>
);

export const DividerClaws = ({ className = '' }: { className?: string }) => (
  <div className={`w-full overflow-hidden relative h-20 ${className}`}>
    <ClawScratchShort className="absolute left-[5%] -top-3 rotate-[6deg]" />
    <ClawScratchShort className="absolute left-[35%] -top-1 rotate-[-10deg] scale-x-[-1]" />
    <ClawScratchShort className="absolute left-[65%] -top-2 rotate-[8deg]" />
    <ClawScratchShort className="absolute left-[90%] -top-1 rotate-[-5deg] scale-x-[-1]" />
  </div>
);

export const DividerBones = ({ className = '' }: { className?: string }) => (
  <div className={`w-full overflow-hidden relative h-10 ${className}`}>
    <BoneIcon className="absolute left-[30%] top-0 rotate-[-12deg] opacity-25 w-8 h-8" />
    <AmmoniteFossil className="absolute left-[55%] -top-1 opacity-20 w-8 h-8" />
    <BoneIcon className="absolute left-[75%] top-1 rotate-[10deg] opacity-25 w-8 h-8" />
  </div>
);
